import {
  Box,
  Button,
  Checkbox as MuiCheckbox,
  Radio as MuiRadio,
  styled,
  useTheme,
} from '@mui/material';
import type { ComponentProps } from 'react';

export const FilterAccordionContainer = ({ children, ...props }: ComponentProps<typeof Box>) => {
  const theme = useTheme();
  return (
    <Box
      {...props}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: theme.spacing(1),
        overflowY: 'auto',
        marginRight: theme.spacing(1),
        padding: theme.spacing(0, 1, 0, 2),
        [theme.breakpoints.down('sm')]: {
          height: 'calc(100vh - 475px)',
          marginRight: theme.spacing(0),
          padding: theme.spacing(0, 2, 0, 2),
        },
      }}
    >
      {children}
    </Box>
  );
};

// TODO: Move to Footer component once all list pages have been updated to use the new filter UI
export const ClearAllButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '12px',
}));

// A 1px line drawn at the right edge of an 8px hit area, so it can be grabbed and focused.
export const FilterListDrag = styled(Box)(({ theme }) => ({
  height: '100%',
  position: 'absolute',
  right: 0,
  top: 0,
  width: '8px',

  '&::after': {
    backgroundColor: theme.palette.borders.main,
    content: '""',
    height: '100%',
    position: 'absolute',
    right: 0,
    top: 0,
    width: '1px',
  },

  '&:hover, &:active': { cursor: 'col-resize' },
  '&:hover::after, &:active::after, &:focus-visible::after': {
    backgroundColor: theme.palette.primary.main,
  },
  '&:focus-visible': theme.mixins.focusRingInset,

  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

export const FilterListSidebar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'width',
})<{
  open: boolean;
  width: number;
}>(({ open, theme, width }) => ({
  backgroundColor: theme.palette.common.white,
  borderRight: open ? `1px solid ${theme.palette.borders.light}` : 'none',
  overflow: 'hidden',
  position: 'relative',
  // A closed sidebar is only clipped to nothing, so hide it too or its controls stay tabbable
  // off-screen. visibility flips at the end of the transition when closing, so it still animates.
  transition: 'height 0.1s ease-in-out, width 0.1s ease-in-out, visibility 0.1s',
  visibility: open ? 'visible' : 'hidden',
  width: open ? `${width}px` : '0px',
  [theme.breakpoints.down('sm')]: {
    height: open ? 'calc(100vh - 425px)' : '0px',
    width: '100%',
    borderBottom: `1px solid ${theme.palette.borders.light}`,
    borderRight: 0,
  },
}));

export const FilterContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minWidth: '250px',
}));

// TODO: Remove me once all list pages have been updated to use the new filter UI
export const LegacyFilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));
// TODO: Remove me once all list pages have been updated to use the new filter UI
export const ClearAllContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  minWidth: '123px',
}));

export const FilterCheckbox = styled(MuiCheckbox)(({ theme }) => ({
  color: theme.palette.primary.dark,
  '&.MuiCheckbox-root': {
    color: theme.palette.primary.dark,
  },
}));

export const FilterRadio = styled(MuiRadio)(({ theme }) => ({
  color: theme.palette.primary.dark,
  '&.MuiRadio-root': {
    color: theme.palette.primary.dark,
  },
}));
